import { useState } from "react";
import { supabase } from "./supabaseclient";

const T = {
  bg: "#06090f",
  panel: "#0b0f1a",
  card: "#0f1623",
  border: "#1a2540",
  gold: "#c9a96e",
  goldLight: "#e8c98a",
  goldDim: "#7a6640",
  text: "#e8eaf0",
  textMid: "#7a8599",
  textDim: "#3d4a5c",
  danger: "#f87171",
  success: "#86efac",
};

export default function Auth() {
  const [mode, setMode]             = useState("signin");
  const [name, setName]             = useState("");
  const [clinicName, setClinicName] = useState("");
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading]       = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [error, setError]           = useState(null);
  const [message, setMessage]       = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // ── Clear state on mode switch ──────────────────────────────────
  function switchMode(newMode) {
    setMode(newMode);
    setError(null);
    setMessage(null);
    setName("");
    setClinicName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  }

  // ── Validation ──────────────────────────────────────────────────
  function validateEmail(val) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  }

  // ── Sign In ─────────────────────────────────────────────────────
  async function handleLogin() {
    setError(null);
    setMessage(null);

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) {
      if (error.message.includes("Invalid login")) {
        setError("Incorrect email or password. Please try again.");
      } else if (error.message.includes("Email not confirmed")) {
        setError("Please verify your email before signing in.");
      } else {
        setError(error.message);
      }
    }

    setLoading(false);
  }

  // ── Sign Up ─────────────────────────────────────────────────────
  async function handleSignup() {
    setError(null);
    setMessage(null);

    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    if (!clinicName.trim()) {
      setError("Please enter your clinic name.");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: {
          full_name: name.trim(),
          clinic_name: clinicName.trim(),
        },
      },
    });

    if (error) {
      if (error.message.includes("already registered")) {
        setError("This email is already registered. Please sign in.");
      } else {
        setError(error.message);
      }
    } else {
      setMessage(
        "✅ Account created! Your 14-day free trial has started. Please check your email to verify your account."
      );
      setPassword("");
      setConfirmPassword("");
    }

    setLoading(false);
  }

  // ── Forgot Password ─────────────────────────────────────────────
  async function handleForgotPassword() {
    setError(null);
    setMessage(null);

    if (!email) {
      setError("Please enter your email address first.");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSendingReset(true);

    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      {
        redirectTo: "https://www.physiocens.com/reset-password",
      }
    );

    if (error) {
      setError(error.message);
    } else {
      setMessage(
        "📧 Password reset link sent! Please check your inbox and spam folder."
      );
    }

    setSendingReset(false);
  }

  // ── Submit Handler ──────────────────────────────────────────────
  function handleSubmit() {
    if (mode === "signin") handleLogin();
    else handleSignup();
  }

  // ── Input Style ─────────────────────────────────────────────────
  const inputStyle = {
    background: T.panel,
    color: T.text,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: "11px 14px",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    outline: "none",
    width: "100%",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.7px",
    textTransform: "uppercase",
    color: T.textDim,
    marginBottom: 5,
  };

  return (
    <div
      style={{
        background: T.bg,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
        padding: 20,
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .auth-input {
          background: #0b0f1a !important;
          color: #e8eaf0 !important;
          border: 1px solid #1a2540 !important;
          border-radius: 8px !important;
          padding: 11px 14px !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 13px !important;
          outline: none !important;
          width: 100% !important;
          transition: border-color 0.2s !important;
        }
        .auth-input:focus {
          border-color: #c9a96e !important;
          box-shadow: 0 0 0 3px rgba(201,169,110,0.08) !important;
        }
        .auth-input::placeholder {
          color: #3d4a5c !important;
        }
        .mode-btn {
          flex: 1;
          border-radius: 8px;
          padding: 11px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .mode-btn:hover {
          opacity: 0.85;
        }
        .submit-btn {
          width: 100%;
          border: none;
          border-radius: 8px;
          padding: 13px;
          font-size: 14px;
          font-weight: 700;
          font-family: 'DM Sans', sans-serif;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .submit-btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .submit-btn:disabled {
          cursor: not-allowed;
          opacity: 0.65;
        }
        .forgot-btn {
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .forgot-btn:hover {
          opacity: 0.75;
        }
        .trust-badge {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          color: #3d4a5c;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .auth-card {
          animation: fadeUp 0.35s ease;
        }
      `}</style>

      <div
        className="auth-card"
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: "44px 40px",
          width: "100%",
          maxWidth: mode === "signup" ? 460 : 420,
          boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
          transition: "max-width 0.3s ease",
        }}
      >
        {/* ── LOGO & BRAND ── */}
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div
            style={{
              width: 86,
              height: 86,
              margin: "0 auto 18px",
              borderRadius: 20,
              backgroundImage: "url('/logo.png')",
              backgroundSize: "150%",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              border: `1px solid ${T.border}`,
            }}
          />
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 38,
              fontWeight: 700,
              color: T.gold,
              letterSpacing: 0.4,
              lineHeight: 1.1,
            }}
          >
            PhysioCens
          </div>
          <div
            style={{
              fontSize: 13,
              color: T.textMid,
              marginTop: 10,
              lineHeight: 1.6,
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontWeight: 500,
              letterSpacing: "0.2px",
            }}
          >
            Clinical Documentation & Physiotherapy Platform
          </div>

          {/* Trial badge - only on signup */}
          {mode === "signup" && (
            <div
              style={{
                marginTop: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 14px",
                borderRadius: 20,
                background: "rgba(201,169,110,0.1)",
                border: `1px solid rgba(201,169,110,0.3)`,
              }}
            >
              <span style={{ fontSize: 11, color: T.gold, fontWeight: 700 }}>
                ✨ 14-Day Free Trial · No Credit Card Required
              </span>
            </div>
          )}
        </div>

        {/* ── MODE TOGGLE ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <button
            className="mode-btn"
            onClick={() => switchMode("signin")}
            style={{
              background:
                mode === "signin"
                  ? `linear-gradient(135deg,${T.gold},${T.goldLight})`
                  : "transparent",
              color: mode === "signin" ? "#07090e" : T.textMid,
              border: `1px solid ${mode === "signin" ? T.gold : T.border}`,
            }}
          >
            Sign In
          </button>
          <button
            className="mode-btn"
            onClick={() => switchMode("signup")}
            style={{
              background:
                mode === "signup"
                  ? `linear-gradient(135deg,${T.gold},${T.goldLight})`
                  : "transparent",
              color: mode === "signup" ? "#07090e" : T.textMid,
              border: `1px solid ${mode === "signup" ? T.gold : T.border}`,
            }}
          >
            Start Free Trial
          </button>
        </div>

        {/* ── ERROR MESSAGE ── */}
        {error && (
          <div
            style={{
              background: "rgba(248,113,113,0.08)",
              border: "1px solid rgba(248,113,113,0.25)",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              color: T.danger,
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* ── SUCCESS MESSAGE ── */}
        {message && (
          <div
            style={{
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.25)",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              color: T.success,
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            {message}
          </div>
        )}

        {/* ── SIGNUP EXTRA FIELDS ── */}
        {mode === "signup" && (
          <>
            {/* Full Name */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Full Name</label>
              <input
                className="auth-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. John Smith"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                autoComplete="name"
              />
            </div>

            {/* Clinic Name */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Clinic Name</label>
              <input
                className="auth-input"
                type="text"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                placeholder="Your Physiotherapy Clinic"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                autoComplete="organization"
              />
            </div>
          </>
        )}

        {/* ── EMAIL ── */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Email Address</label>
          <input
            className="auth-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={
              mode === "signup"
                ? "admin@yourclinic.com"
                : "Enter your email"
            }
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoComplete="email"
          />
        </div>

        {/* ── PASSWORD ── */}
        <div style={{ marginBottom: mode === "signup" ? 14 : 22 }}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: "relative" }}>
            <input
              className="auth-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                mode === "signup"
                  ? "Min 8 characters"
                  : "••••••••"
              }
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoComplete={
                mode === "signup" ? "new-password" : "current-password"
              }
              style={{ paddingRight: 42 }}
            />
            {/* Show/Hide password toggle */}
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.textDim,
                fontSize: 14,
                padding: 0,
              }}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>

          {/* Password strength indicator - signup only */}
          {mode === "signup" && password.length > 0 && (
            <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
              {[1, 2, 3, 4].map((level) => {
                const strength =
                  password.length >= 12 &&
                  /[A-Z]/.test(password) &&
                  /[0-9]/.test(password) &&
                  /[^A-Za-z0-9]/.test(password)
                    ? 4
                    : password.length >= 10 &&
                      /[A-Z]/.test(password) &&
                      /[0-9]/.test(password)
                    ? 3
                    : password.length >= 8
                    ? 2
                    : 1;

                const colors = ["", "#ef4444", "#fbbf24", "#22d3ee", "#10b981"];
                return (
                  <div
                    key={level}
                    style={{
                      flex: 1,
                      height: 3,
                      borderRadius: 2,
                      background:
                        level <= strength ? colors[strength] : T.border,
                      transition: "background 0.3s",
                    }}
                  />
                );
              })}
              <span
                style={{
                  fontSize: 10,
                  color: T.textDim,
                  marginLeft: 4,
                  whiteSpace: "nowrap",
                }}
              >
                {password.length < 8
                  ? "Too short"
                  : password.length >= 12 &&
                    /[A-Z]/.test(password) &&
                    /[0-9]/.test(password)
                  ? "Strong 💪"
                  : "Good"}
              </span>
            </div>
          )}

          {/* Forgot Password - signin only */}
          {mode === "signin" && (
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <button
                type="button"
                className="forgot-btn"
                onClick={handleForgotPassword}
                disabled={sendingReset}
                style={{
                  color: T.gold,
                  fontSize: 12,
                  opacity: sendingReset ? 0.6 : 1,
                }}
              >
                {sendingReset ? "Sending reset link..." : "Forgot Password?"}
              </button>
            </div>
          )}
        </div>

        {/* ── CONFIRM PASSWORD - signup only ── */}
        {mode === "signup" && (
          <div style={{ marginBottom: 22 }}>
            <label style={labelStyle}>Confirm Password</label>
            <input
              className="auth-input"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoComplete="new-password"
            />
            {/* Match indicator */}
            {confirmPassword.length > 0 && (
              <div
                style={{
                  fontSize: 11,
                  marginTop: 5,
                  color:
                    password === confirmPassword ? "#10b981" : "#ef4444",
                }}
              >
                {password === confirmPassword
                  ? "✓ Passwords match"
                  : "✕ Passwords do not match"}
              </div>
            )}
          </div>
        )}

        {/* ── SUBMIT BUTTON ── */}
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={loading}
          style={{
            background: `linear-gradient(135deg,${T.gold},${T.goldLight})`,
            color: "#07090e",
          }}
        >
          {loading
            ? mode === "signin"
              ? "Signing in..."
              : "Creating your account..."
            : mode === "signin"
            ? "Sign In to PhysioCens"
            : "Start My Free Trial →"}
        </button>

        {/* ── TRUST SIGNALS ── */}
        {mode === "signup" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginTop: 16,
            }}
          >
            {[
              { icon: "🔒", text: "Secure & Encrypted" },
              { icon: "📋", text: "GST Invoice Ready" },
              { icon: "❌", text: "No Credit Card" },
              { icon: "✅", text: "Cancel Anytime" },
            ].map((item) => (
              <div key={item.text} className="trust-badge">
                <span>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── FOOTER TEXT ── */}
        <div
          style={{
            textAlign: "center",
            marginTop: 20,
            fontSize: 12,
            color: T.textDim,
            lineHeight: 1.7,
          }}
        >
          {mode === "signin" ? (
            <>
              <div>Don't have an account?{" "}
                <button
                  onClick={() => switchMode("signup")}
                  style={{
                    background: "none",
                    border: "none",
                    color: T.gold,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Start free trial
                </button>
              </div>
              <div style={{ marginTop: 8 }}>
                Need help?{" "}
                <a
                  href="mailto:support@physiocens.com"
                  style={{
                    color: T.gold,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  support@physiocens.com
                </a>
              </div>
            </>
          ) : (
            <>
              <div>
                Already have an account?{" "}
                <button
                  onClick={() => switchMode("signin")}
                  style={{
                    background: "none",
                    border: "none",
                    color: T.gold,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Sign in
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 11 }}>
                By signing up you agree to our{" "}
                <a
                  href="https://www.physiocens.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: T.gold, textDecoration: "none" }}
                >
                  Terms
                </a>{" "}
                &{" "}
                <a
                  href="https://www.physiocens.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: T.gold, textDecoration: "none" }}
                >
                  Privacy Policy
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}